import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Contexts

import { ContextProvider, Context } from '../../../../../contexts/ssapSummaryContext';

// Selectors

import {
    selectIfExtraFinalSummaryShown,
    selectIfFinalReviewPageActive,
    selectIfOnlyUpdatedFieldsShown,
    selectIfPreviousDataLoaded
} from '../../../../../reducers/appReducer';

// Types

import { SsapSummaryType } from '../../../../../components/ssap/SsapTypes';

interface SummaryFilterProps {
    children: ReactElement | Array<ReactElement>,
    indexMapper?: object,
    summaryId: SsapSummaryType
}

const SummaryFilter = (props: SummaryFilterProps): JSX.Element | null => {
    const {
        children,
        indexMapper,
        summaryId
    } = props;


    const {
        contexts: parentContexts,
        indexMapper: indexMapperFromContext,
    } = useContext(Context);

    const areOnlyUpdatedFieldsShown: boolean = useSelector(selectIfOnlyUpdatedFieldsShown);
    const isExtraFinalSummaryShown = useSelector(selectIfExtraFinalSummaryShown);
    const isFinalReviewPageActive: boolean = useSelector(selectIfFinalReviewPageActive);
    const isPreviousDataLoaded: boolean = useSelector(selectIfPreviousDataLoaded);

    const [ isShown, showSummary ] = useState<boolean>(
        !isFinalReviewPageActive || (isFinalReviewPageActive && !isExtraFinalSummaryShown)
    );

    const [ registeredSummaryItemsWithDiffs, updateRegistered ] = useState([]);

    useEffect(() => {
        parentContexts.forEach(context => {
            const {
                updateRegistered
            } = context;

            if(typeof updateRegistered === 'function') {
                updateRegistered(registered => [ ...registered, ...registeredSummaryItemsWithDiffs ]);
            }
        });
    }, [ registeredSummaryItemsWithDiffs ]);

    useEffect((): void => {
        if (isPreviousDataLoaded) {
            showSummary(true);
        }
    }, [ isPreviousDataLoaded ]);

    if (!isShown) {
        return null;
    }

    if (isExtraFinalSummaryShown && areOnlyUpdatedFieldsShown && registeredSummaryItemsWithDiffs.length === 0) {
        return null;
    }

    return (
        <ContextProvider
            propsToInject = { {
                contexts: [
                    ...parentContexts,
                    {
                        summaryId,
                        updateRegistered
                    }
                ],
                indexMapper: indexMapper ? indexMapper: indexMapperFromContext,
                registeredSummaryItemsWithDiffs,
                summaryId,
                summaryItemsComparisonEnabled: isExtraFinalSummaryShown && isFinalReviewPageActive,
                updateRegistered
            } }
        >
            { children }
        </ContextProvider>
    );
};

export default SummaryFilter;